import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MessageCircle } from "lucide-react";
import FunnelLayout from "@/components/app/FunnelLayout";
import ExitIntentModal from "@/components/app/ExitIntentModal";
import QuizLink from "@/components/app/QuizLink";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { track } from "@/lib/analytics";

const STAGES = [
  {
    num: 1,
    title: "עוצרים את הדימום",
    text: 'החוק הראשון בבור: מפסיקים לחפור. הלוואה לכיסוי מינוס רק מעמיקה את הבור. מרגע זה, מפסיקים "לגלגל" הלוואות ולקחת אשראי מהיר ואגרסיבי שסוגר פינה היום וחונק אותנו מחר.',
  },
  {
    num: 2,
    title: "הנדסה מחדש (איחוד)",
    text: 'כאן קורה הקסם. לוקחים את כל "המשכנתה השנייה" (רכבים, אשראי, מינוס) ופורסים אותה מחדש, אידיאלית כחלק מהמשכנתה הקיימת על הנכס. פער הריביות חותך את ההחזר החודשי במאות אחוזים.',
  },
  {
    num: 3,
    title: "שיטת המעטפות",
    text: 'הכסף שהתפנה (ה"חמצן") לא יכול להיבלע חזרה בעו"ש. מחלקים אותו מראש ב-1 לחודש: חלק לחיסכון, חלק לבלת"מים, וכן – גם חלק לחופשות ולהנאות (בלי רגשות אשם!).',
  },
];

const PSYCHOLOGY = [
  {
    num: "1.",
    title: "סוף לבושה הזוגית",
    text: "הסוד הכי גדול לחשבון בנק בריא הוא תקשורת. להפסיק להסתיר הוצאות מהשותף שלכם. ברגע שמבינים ששניכם באותה סירה ומול אותה מערכת, הופכים לצוות מנצח במקום לאויבים.",
  },
  {
    num: "2.",
    title: 'לדעת להגיד "לא"',
    text: '"כולם טסים לדובאי, מה איתנו?". כולם טסים לדובאי ולוקחים הלוואות כדי לממן את זה. חלק משבירת הכלים היא להבין שהסטטוס החברתי לא שווה את החרדה בלילה. קודם משקמים את התזרים, אחר כך טסים (מתוך המעטפה הייעודית!).',
  },
];

export default function FunnelGuide3() {
  const navigate = useNavigate();
  const [debt, setDebt] = useState(6000);
  const oxygen = useMemo(() => Math.round(debt * 0.75), [debt]);

  const [pleasure, setPleasure] = useState(33);
  const [contingency, setContingency] = useState(33);
  const [future, setFuture] = useState(34);
  const sum = pleasure + contingency + future;

  const handleSlider = (which, val) => {
    val = Math.max(0, Math.min(100, Math.round(val)));
    let p = pleasure, c = contingency, f = future;
    if (which === "p") p = val;
    if (which === "c") c = val;
    if (which === "f") f = val;
    const remaining = 100 - val;
    if (which === "p") {
      const other = c + f || 1;
      c = Math.round((c / other) * remaining);
      f = 100 - val - c;
    } else if (which === "c") {
      const other = p + f || 1;
      p = Math.round((p / other) * remaining);
      f = 100 - val - p;
    } else {
      const other = p + c || 1;
      p = Math.round((p / other) * remaining);
      c = 100 - val - p;
    }
    setPleasure(Math.max(0, p));
    setContingency(Math.max(0, c));
    setFuture(Math.max(0, f));
  };

  const amt = (pct) => Math.round((oxygen * pct) / 100);

  return (
    <FunnelLayout
      step={3}
      title="הפתרון התזרימי"
      loadingMessages={["טוען את הסימולטור...", "מחשב את החמצן שלך..."]}
    >
      {(lead) => (
        <>
          {/* Section 1: 3 stages */}
          <div className="bg-white rounded-3xl p-6 md:p-8 border border-black/5 shadow-[0_8px_30px_rgba(0,0,0,0.04)] mb-10">
            <h2 className="text-2xl md:text-3xl font-[800] mb-3">3 השלבים ליציאה מהמינוס הכרוני</h2>
            <p className="text-stone-600 leading-[1.7] mb-8">
              השיטה של "אמת או חובות" לא מבוססת על קסמים, אלא על הנדסה פיננסית חכמה והחלטות אמיצות.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              {STAGES.map((s) => (
                <motion.div
                  key={s.num}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: s.num * 0.1 }}
                  className="bg-stone-50 rounded-2xl p-6 border border-stone-200 hover:-translate-y-1 transition-all"
                  data-testid={`stage-${s.num}`}
                >
                  <div className="w-12 h-12 rounded-full bg-brand-charcoal text-white flex items-center justify-center font-[800] text-xl mb-4">
                    {s.num}
                  </div>
                  <h3 className="text-xl font-[800] mb-2">{s.title}</h3>
                  <p className="text-sm text-stone-600 leading-[1.7]">{s.text}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Section 2: Oxygen simulator */}
          <div className="bg-white rounded-3xl p-6 md:p-8 border border-black/5 mb-8">
            <h3 className="text-xl md:text-2xl font-[800] mb-2">סימולטור יצירת החמצן</h3>
            <p className="text-stone-600 mb-6 text-sm leading-[1.6]">
              זוכרים את הסכום שיצא לכם במחשבון המיפוי במדריך הקודם? הזינו אותו כאן כדי לראות מה תעשו עם הכסף שיתפנה בעקבות ארגון מחדש.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 items-center">
              <div>
                <label className="text-sm font-bold mb-2 block">סך ההחזרים שמצאתם במדריך 2 (₪):</label>
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-[800] text-brand-amber">₪</span>
                  <Input
                    type="number"
                    value={debt}
                    onChange={(e) => setDebt(Number(e.target.value) || 0)}
                    data-testid="debt-input"
                    className="h-14 text-2xl font-[800] rounded-xl"
                  />
                </div>
              </div>
              <div className="bg-emerald-50 rounded-2xl p-5 border border-emerald-200">
                <div className="text-sm text-stone-700 mb-1 leading-relaxed">
                  לאחר איחוד ופריסה מחדש (הערכה),<br />
                  סכום החמצן החודשי החדש שלכם יהיה:
                </div>
                <div className="text-4xl font-[800] text-emerald-600 mt-2" data-testid="new-oxygen">
                  ₪{oxygen.toLocaleString()}
                </div>
              </div>
            </div>
            <p className="text-xs text-stone-500 mt-4 leading-relaxed">
              * ההערכה מבוססת על הפחתה ממוצעת של 75% בהחזרים החודשיים של חובות צרכניים קצרים בפריסה ארוכת טווח.
            </p>
          </div>

          {/* Section 3: Envelope sliders */}
          <div className="bg-white rounded-3xl p-6 md:p-8 border border-black/5 mb-10">
            <h3 className="text-xl md:text-2xl font-[800] mb-2">חלקו את החמצן שלכם</h3>
            <p className="text-stone-600 mb-6 text-sm leading-[1.6]">
              לשחק עם הכסף שהתפנה. שנו את הסליידרים (חייב להסתכם ל-100%).
            </p>

            <SliderRow
              testId="slider-pleasure"
              category="הנאה"
              label="מותרות וחופשות (בלי רגשות אשם)"
              value={pleasure}
              onChange={(v) => handleSlider("p", v)}
              amount={amt(pleasure)}
              color="#d97706"
            />
            <SliderRow
              testId="slider-contingency"
              category="בלת״מים"
              label='קופת בלת"מים (רכב, שיניים)'
              value={contingency}
              onChange={(v) => handleSlider("c", v)}
              amount={amt(contingency)}
              color="#44403c"
            />
            <SliderRow
              testId="slider-future"
              category="עתיד"
              label="השקעה וחיסכון עתידי"
              value={future}
              onChange={(v) => handleSlider("f", v)}
              amount={amt(future)}
              color="#10b981"
            />

            <div
              className={`mt-6 text-center font-bold py-3 rounded-xl ${
                sum === 100 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
              }`}
              data-testid="slider-sum"
            >
              סך האחוזים חייב להיות בדיוק 100%. (כרגע: {sum}%)
            </div>
          </div>

          {/* Section 4: Psychology of success */}
          <div className="bg-brand-stone text-white rounded-3xl p-6 md:p-8 mb-10 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-brand-amber/20 blur-3xl" />
            <div className="relative">
              <h2 className="text-2xl md:text-3xl font-[800] mb-6">הפסיכולוגיה של ההצלחה</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {PSYCHOLOGY.map((p) => (
                  <div key={p.title} className="bg-white/5 rounded-2xl p-5 border border-white/10">
                    <h3 className="text-lg font-[800] mb-2">
                      <span className="text-brand-amber">{p.num}</span> {p.title}
                    </h3>
                    <p className="text-sm text-stone-300 leading-[1.7]">{p.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Section 5: Now it's your turn */}
          <div className="bg-white rounded-3xl p-6 md:p-8 border border-black/5 mb-10">
            <h2 className="text-2xl md:text-3xl font-[800] mb-3">עכשיו תורכם לפעול.</h2>
            <p className="text-stone-700 leading-[1.7] mb-6">
              יש לכם את הידע. אתם מבינים את המלכודת, מיפיתם את החובות, ואתם יודעים שיש פתרון. <strong className="text-brand-amber">אל תשאירו את זה בגדר תיאוריה.</strong> הצעד הבא שלכם קובע איך ייראה העשור הקרוב של המשפחה שלכם.
            </p>
            <h3 className="text-lg font-[800] mb-3">מסלול ההמראה שלכם:</h3>
            <ul className="space-y-3 mb-6">
              {[
                { id: "map", content: "וודאו שמיפיתם את כלל ההלוואות (מדריך 2)." },
                { id: "quiz", content: <>מלאו את <QuizLink lead={lead} source="guide3_takeoff">שאלון האבחון שלנו</QuizLink> כדי לבדוק זכאות לאיחוד.</> },
                { id: "community", content: "הצטרפו לקהילת הווצאפ לקבלת טיפים, תמיכה וסיפורי הצלחה." },
              ].map((step) => (
                <li key={step.id} className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full bg-brand-amber mt-2.5 flex-shrink-0" />
                  <span className="text-stone-700 leading-[1.6]">{step.content}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={lead.affiliate_url}
                target="_blank"
                rel="noreferrer"
                data-testid="guide3-affiliate-btn"
                onClick={() =>
                  track("funnel_affiliate_click", {
                    campaignId: lead.campaign_id,
                    leadId: lead.lead_id,
                    payload: { source: "guide3_questionnaire" },
                  })
                }
                className="flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-full bg-brand-amber hover:bg-brand-amberHover text-white font-bold transition active:scale-95"
              >
                לשאלון האבחון החינמי
              </a>
              <a
                href={lead.whatsapp_url || "#"}
                target="_blank"
                rel="noreferrer"
                data-testid="guide3-whatsapp-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-full bg-white border-2 border-stone-200 hover:border-stone-300 text-brand-charcoal font-bold transition"
              >
                <MessageCircle className="w-4 h-4" />
                לקהילה הסגורה
              </a>
            </div>
          </div>

          {/* Next */}
          <div className="flex flex-col items-center gap-3 py-6">
            <Button
              data-testid="funnel-next-btn"
              onClick={() => navigate(`/funnel/${lead.lead_id}/4`)}
              className="h-14 px-10 rounded-full bg-brand-amber hover:bg-brand-amberHover text-white font-bold pulse-cta"
            >
              למדריך האחרון: מהישרדות לצמיחה
              <ArrowLeft className="w-5 h-5 me-2" />
            </Button>
          </div>
          <ExitIntentModal lead={lead} source="guide3" />
        </>
      )}
    </FunnelLayout>
  );
}

function SliderRow({ category, label, value, onChange, amount, color, testId }) {
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <span className="inline-block px-3 py-1 rounded-full text-xs font-[800] text-white" style={{ background: color }}>{category}</span>
          <span className="me-3 text-sm text-stone-700 font-bold">{label}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold text-stone-500">{value}%</span>
          <span className="font-[800]" style={{ color }}>
            ₪{amount.toLocaleString()}
          </span>
        </div>
      </div>
      <Slider
        data-testid={testId}
        value={[value]}
        onValueChange={(v) => onChange(v[0])}
        max={100}
        step={1}
        className="cursor-pointer"
      />
    </div>
  );
}
